import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { Loader2, Bot, Trash2 } from 'lucide-react';
import apiClient from '@/lib/apiClient';

interface BitrixBotManagerProps {
  channelId: number;
  isActive: boolean;
  botInstalled: boolean;
  onStatusChange?: () => void;
}

type BotStatusResponse = { canInstall: boolean; botInstalled: boolean; reason?: string };

export const BitrixBotManager: React.FC<BitrixBotManagerProps> = ({ channelId, isActive, botInstalled, onStatusChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canInstall, setCanInstall] = useState<boolean>(true);
  // Inicializa botStatus con el valor de la prop botInstalled para mostrar el botón desde el inicio
  const [botStatus, setBotStatus] = useState<{ botInstalled: boolean; reason?: string }>({ botInstalled: !!botInstalled });

  // Debug: Verificar si el componente se monta y props llegan correctamente
  useEffect(() => {
    console.log('[BitrixBotManager] MOUNT', { channelId, isActive, botInstalled });
  }, [channelId, isActive, botInstalled]);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      try {
        const res: BotStatusResponse = await apiClient.post('/bitrix/bot/status', { channelId });
        console.log('Bot status response:', res);
        if (mounted) {
          setCanInstall(!!res.canInstall);
          setBotStatus({ botInstalled: !!res.botInstalled, reason: res.reason });
        }
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchStatus();
    return () => { mounted = false; };
  }, [channelId]);

  const handleInstall = async () => {
    setLoading(true);
    setError(null);
    try {
  // Genera un botCode único por canal
  const botCode = `whatsapp-bot-${channelId}`;
  const name = t('whatsappBotChannel', { channelId });
  await apiClient.post('/bitrix/bot/register', { channelId, botCode, name });
      onStatusChange?.();
      const res: BotStatusResponse = await apiClient.post('/bitrix/bot/status', { channelId });
      setCanInstall(!!res.canInstall);
      setBotStatus({ botInstalled: !!res.botInstalled, reason: res.reason });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUninstall = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/bitrix/bot/unregister', { channelId });
      onStatusChange?.();
      const res: BotStatusResponse = await apiClient.post('/bitrix/bot/status', { channelId });
      setCanInstall(!!res.canInstall);
      setBotStatus({ botInstalled: !!res.botInstalled, reason: res.reason });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isActive) return null;

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
      {botStatus?.botInstalled ? (
        <Button variant="destructive" size="sm" onClick={handleUninstall} disabled={loading} title={t('uninstallBitrixChatbot')}>
          {loading ? <Loader2 className="animate-spin w-4 h-4 mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
          {t('uninstallChatbot')}
        </Button>
      ) : (
        <Button variant="default" size="sm" onClick={handleInstall} disabled={loading || !canInstall} title={t('installBitrixChatbot')}>
          {loading ? <Loader2 className="animate-spin w-4 h-4 mr-1" /> : <Bot className="w-4 h-4 mr-1" />}
          {t('installChatbot')}
        </Button>
      )}
      {botStatus?.reason && (
        <span className="text-xs text-gray-500">{botStatus.reason}</span>
      )}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};